import React from 'react';
import PropTypes from 'prop-types';

import AutoLayoutsToggle from './components/AutoLayoutsToggle';
import Button from '../../../../../../../../../../components/global/Button';
import {
  ALBUM_SLIDESHOW_MODE,
  ALBUM_SLIDESHOW_STATUS,
} from '../../../../../../../../../../constants/albums';
import SlidesSection from './components/SlidesSection/SlidesSection';
import {
  isAutoLayoutsSlideshow,
  isSelectedPhotosSlideshow,
} from '../../../../../../../../../../util/album';
import useOpenAlert from '../../../../../../../../../../hooks/useOpenAlert';

const propTypes = {
  saving: PropTypes.bool.isRequired,
  slideshow: PropTypes.shape({
    mode: PropTypes.oneOf(Object.values(ALBUM_SLIDESHOW_MODE)),
    status: PropTypes.oneOf(Object.values(ALBUM_SLIDESHOW_STATUS)),
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
    ticker: PropTypes.shape({
      message: PropTypes.string,
      color: PropTypes.string,
      background_color: PropTypes.string,
    }),
    intro_layout: PropTypes.object,
    intro_asset_image: PropTypes.object,
    excluded_photos: PropTypes.shape({ total: PropTypes.number }),
    selected_photos: PropTypes.shape({ total: PropTypes.number }),
  }).isRequired,
  slideshowSlides: PropTypes.array.isRequired,
  slidesTotal: PropTypes.number.isRequired,
  slidesOffset: PropTypes.number.isRequired,
  currentSlideId: PropTypes.string.isRequired,
  hasIntroSlide: PropTypes.bool.isRequired,
  onIntroSlideCreate: PropTypes.func.isRequired,
  onSlideshowUpdate: PropTypes.func.isRequired,
  loadingMore: PropTypes.bool.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  onDeletePhoto: PropTypes.func.isRequired,
  onMoveSlide: PropTypes.func.isRequired,
  onReorderSlides: PropTypes.func.isRequired,
  onDeleteLayout: PropTypes.func.isRequired,
  onThumbnailClick: PropTypes.func.isRequired,
  onStartFromScratch: PropTypes.func.isRequired,
};

const SlidesStep = ({
  saving,
  slideshow,
  slideshowSlides,
  slidesTotal,
  slidesOffset,
  currentSlideId,
  hasIntroSlide,
  onIntroSlideCreate,
  onSlideshowUpdate,
  loadingMore,
  onLoadMore,
  onDeletePhoto,
  onMoveSlide,
  onReorderSlides,
  onDeleteLayout,
  onThumbnailClick,
  onStartFromScratch,
}) => {
  const openAlert = useOpenAlert();

  const onAlbumSlideshowModeToggle = () => {
    if (saving) {
      return;
    }

    if (!slideshow) {
      return;
    }

    const isAutoLayouts = isAutoLayoutsSlideshow(slideshow);
    const isSelectedPhotos = isSelectedPhotosSlideshow(slideshow);

    if (!isAutoLayouts) {
      onSlideshowUpdate({
        mode: isSelectedPhotos
          ? ALBUM_SLIDESHOW_MODE.SELECTED_PHOTO_LAYOUTS
          : ALBUM_SLIDESHOW_MODE.AUTO_LAYOUTS,
        createAutoLayouts: !isSelectedPhotos,
        createSelectedPhotoLayouts: isSelectedPhotos,
      });
    } else {
      onSlideshowUpdate({
        mode: isSelectedPhotos
          ? ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS
          : ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
      });
    }
  };

  const showStartFromScratch = (() => {
    if (!slideshow) {
      return false;
    }
    if ((slideshow.excluded_photos?.total ?? 0) > 0) {
      return true;
    }
    if ((slideshow.selected_photos?.total ?? 0) > 0) {
      return true;
    }
    if (
      slideshow.ticker?.message ||
      slideshow.ticker?.color ||
      slideshow.ticker?.background_color
    ) {
      return true;
    }
    if (slideshow.intro_layout) {
      return true;
    }
    if (slideshow.intro_asset_image) {
      return true;
    }
    if (slideshow.mode && slideshow.mode !== ALBUM_SLIDESHOW_MODE.ALL_PHOTOS) {
      return true;
    }
    if (slideshow.status && slideshow.status !== ALBUM_SLIDESHOW_STATUS.DRAFT) {
      return true;
    }
    if (slideshow.qr_code?.is_enabled) {
      return true;
    }
    if (
      slideshow.slide_background_asset_image ||
      (slideshow.slide_background_color &&
        slideshow.slide_background_color !== '#5c88b2')
    ) {
      return true;
    }
    if (slideshow.slide_duration_ms !== 3000) {
      return true;
    }
    return false;
  })();

  const onStartFromScratchClick = () => {
    openAlert({
      type: 'info',
      dismissible: false,
      actionSize: 'sm',
      message: ({ onRequestClose: onAlertClose }) => (
        <div className="text-center">
          <h3 className="text-center">Discard and start over?</h3>
          <p className="text-center text-light">
            This will clear all current content and cannot be undone. Are you
            sure you want to continue?
          </p>
          <Button
            btnStyle="danger"
            btnOutline
            btnBlock
            className="mb-2"
            onClick={async () => {
              onAlertClose();
              await onStartFromScratch();
            }}
          >
            Yes, I want to start from scratch
          </Button>
          <Button
            btnStyle="link"
            className="text-extra-bold"
            onClick={onAlertClose}
          >
            Go back
          </Button>
        </div>
      ),
    });
  };

  return (
    <div className="secondary-photos-view-slides">
      {showStartFromScratch && (
        <Button
          btnStyle="danger"
          btnOutline
          btnBlock
          className="mb-2"
          disabled={saving}
          onClick={onStartFromScratchClick}
        >
          ↺ Start from scratch
        </Button>
      )}
      <AutoLayoutsToggle
        disabled={saving}
        onToggleAutoLayoutsMode={onAlbumSlideshowModeToggle}
        albumSlideshowMode={slideshow?.mode}
      />
      {!hasIntroSlide && onIntroSlideCreate && (
        <div className="mb-2">
          <p className="text-uppercase text-light text-extra-bold text-xs mb-1">
            Cover Slide
          </p>
          <Button
            btnStyle="reset"
            onClick={onIntroSlideCreate}
            className="secondary-photos-view-slides-step-create"
          >
            Create
          </Button>
        </div>
      )}
      <SlidesSection
        hasIntroSlide={hasIntroSlide}
        slidesTotal={slidesTotal}
        slidesOffset={slidesOffset}
        currentSlideId={currentSlideId}
        loadingMore={loadingMore}
        onLoadMore={onLoadMore}
        onDeletePhoto={onDeletePhoto}
        slideshowSlides={slideshowSlides}
        onMoveSlide={onMoveSlide}
        onReorderSlides={onReorderSlides}
        onDeleteLayout={onDeleteLayout}
        onThumbnailClick={onThumbnailClick}
      />
    </div>
  );
};

SlidesStep.propTypes = propTypes;

export default SlidesStep;
